import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { currency, fmtDate } from "../lib/constants";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const SETTLE = {
  settled: { label: "Settled to bank", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: "CheckCircle2" },
  in_transit: { label: "On its way to bank", cls: "bg-sky-100 text-sky-700 border-sky-200", icon: "Truck" },
  in_balance: { label: "In Stripe balance", cls: "bg-indigo-100 text-indigo-700 border-indigo-200", icon: "Wallet" },
  pending: { label: "Awaiting settlement", cls: "bg-amber-100 text-amber-700 border-amber-200", icon: "Clock" },
  failed: { label: "Payout failed", cls: "bg-red-100 text-red-700 border-red-200", icon: "AlertTriangle" },
};

const PAY = {
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700" },
  expired: { label: "Expired", cls: "bg-muted text-muted-foreground" },
};

const SettleBadge = ({ status }) => {
  const s = SETTLE[status] || SETTLE.pending;
  const Icon = Icons[s.icon] || Icons.Clock;
  return (
    <span data-testid={`settlement-badge-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${s.cls}`}>
      <Icon size={13} /> {s.label}
    </span>
  );
};

const StatCard = ({ label, value, sub, testid }) => (
  <div className="bg-card border border-border rounded-md p-4 shadow-sm" data-testid={testid}>
    <div className="label-caps text-muted-foreground">{label}</div>
    <div className="text-2xl font-head font-extrabold mt-1">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const PAGE = 200;

  const load = () =>
    api.get(`/payments?limit=${PAGE}&skip=0`).then((r) => { setRows(r.data.payments || []); setSummary(r.data.summary || null); setTotal(r.data.total || 0); })
      .catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/payments?limit=${PAGE}&skip=${rows.length}`);
      setRows((prev) => [...prev, ...(data.payments || [])]);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoadingMore(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/payments/refresh-settlements");
      toast.success(`Checked ${data.checked} payment(s) with Stripe — ${data.updated} updated`);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-head font-extrabold tracking-tight">Payments</h1>
          <p className="text-muted-foreground">Every deposit, its payment status and whether Stripe has settled it to your bank.</p>
        </div>
        <button onClick={refresh} disabled={refreshing} data-testid="payments-refresh"
          className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all disabled:opacity-50">
          <Icons.RefreshCw size={17} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Checking Stripe…" : "Refresh settlements"}
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="stat-paid" label="Collected (paid)" value={currency(summary.paid_amount)} sub={`${summary.paid_count} payment(s)`} />
          <StatCard testid="stat-settled" label="Settled to bank" value={currency(summary.settled_amount)} sub={`${summary.settled_count} payment(s)`} />
          <StatCard testid="stat-awaiting" label="Awaiting settlement" value={currency(summary.awaiting_settlement)} sub="Usually 2–3 business days" />
          <StatCard testid="stat-total" label="All transactions" value={summary.total} sub="Includes pending / failed" />
        </div>
      )}

      <div className="bg-card border border-border rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="payments-table">
          <thead>
            <tr className="text-left label-caps border-b border-border">
              <th className="p-3">Customer</th><th>Quote</th><th>Amount</th><th>Payment</th><th>Settlement</th><th>Bank arrival</th><th>Created</th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground" data-testid="payments-empty">No payments yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors" data-testid={`payment-row-${r.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{r.lead_name}</div>
                  <div className="text-xs text-muted-foreground">{r.lead_email || r.lead_phone || ""}</div>
                </td>
                <td className="text-primary font-semibold cursor-pointer" data-testid={`payment-quote-${r.id}`} onClick={() => r.lead_id && navigate(`/app/leads/${r.lead_id}`)}>{r.quote_no || "—"}</td>
                <td className="font-semibold">{currency(r.amount)}</td>
                <td><span data-testid={`payment-badge-${r.id}`} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${(PAY[r.payment_status] || PAY.pending).cls}`}>{(PAY[r.payment_status] || PAY.pending).label}</span></td>
                <td>{r.payment_status === "paid" ? <SettleBadge status={r.settlement_status || "pending"} /> : <span className="text-xs text-muted-foreground">—</span>}</td>
                <td className="text-xs text-muted-foreground">{r.payout_arrival_date ? fmtDate(new Date(r.payout_arrival_date * 1000).toISOString()) : "—"}</td>
                <td className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</td>
                <td>{r.receipt_url && <a href={r.receipt_url} target="_blank" rel="noreferrer" className="text-secondary" title="Stripe receipt" data-testid={`payment-receipt-${r.id}`}><Icons.ExternalLink size={15} /></a>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length < total && (
        <div className="flex justify-center">
          <button onClick={loadMore} disabled={loadingMore} data-testid="payments-load-more"
            className="px-4 py-2 rounded-md border border-border font-semibold text-sm hover:bg-muted disabled:opacity-50">
            {loadingMore ? "Loading…" : `Load more (${rows.length} of ${total})`}
          </button>
        </div>
      )}

      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Icons.Info size={13} /> Stripe typically settles funds to your nominated bank account 2–3 business days after a payment. The green “Settled to bank” tick appears once Stripe confirms the payout is paid.
      </p>
    </div>
  );
}
